import {
  createContext,
  useReducer,
  useContext,
  FC,
  useCallback,
  useEffect,
  useState
} from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserData } from 'store/user/selectors';
import { useSearchParams } from 'react-router-dom';
import { addOrganizationsInfo } from 'store/analytics/slice';
import {
  fetchOrganizationsForPicker,
  fetchSubOrganizations
} from 'services/api/organizations';

import useIsSuperAdminRole from 'hooks/useIsSuperAdminRole';
import reducer from './reducers';
import {
  State,
  Dispatch,
  DispatchContext,
  IOrganizationsProps,
  ActionTypes
} from './types';
import { parentOrgStorage, subOrgStorage } from 'utils/storage';
import { Loading } from 'components/ui';

const initialState: State = {
  parentSelected: parentOrgStorage.get() || '',
  subSelected: '',
  selected: '',
  results: [],
  subResults: [],
  orgVersion: 0
};

const OrganizationsStateContext = createContext<State | undefined>(undefined);
const OrganizationsDispatchContext = createContext<Dispatch | undefined>(
  undefined
);

const OrganizationsProvider: FC<IOrganizationsProps> = ({ children }) => {
  const data = useAppSelector(selectUserData());
  const [isLoading, setLoading] = useState(true);

  // Gates the 'All organizations' option. Keyed on the super_admin ROLE:
  // the backend decides the same way, and only it may act across all orgs.
  const isAdmin = useIsSuperAdminRole();

  const dispatchRedux = useAppDispatch();

  const [state, dispatch] = useReducer(reducer, {
    ...initialState
  });

  // Parent organizations list — fetched once on user load.
  useEffect(() => {
    if (!data) return;
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchOrganizationsForPicker();
        if (cancelled) return;

        const results = [
          ...(isAdmin ? [{ label: 'All', value: 'all' as const }] : []),
          ...rows.map(item => ({
            // Org name is jsonb Record<locale, string>; show en for the picker.
            label: (item.name?.en as string) ?? '',
            icon: (item.image as string) ?? undefined,
            value: String(item.id)
          }))
        ];

        // analytics slice stores the picker rows as-is — the page table
        // re-fetches its own full rows via fetchOrganizations().
        dispatchRedux(addOrganizationsInfo(rows as never));
        dispatch({ type: ActionTypes.SET_ORGANIZATIONS, data: results });

        let parentOrganizationValue: string | undefined;
        const storedValue = parentOrgStorage.get();
        const existing = results.find(item => item.value === storedValue);

        if (existing) {
          parentOrganizationValue = existing.value;
        } else {
          const firstValue = results?.[0]?.value;

          if (firstValue) {
            parentOrgStorage.set(firstValue);
            parentOrganizationValue = firstValue;
          }
        }

        if (parentOrganizationValue) {
          dispatch({
            type: ActionTypes.SELECT_PARENT_ORGANIZATION,
            data: parentOrganizationValue
          });
        }

        if (parentOrganizationValue === 'all') {
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [data, isAdmin, dispatchRedux]);

  // Sub-organizations when a non-'all' parent is selected.
  useEffect(() => {
    if (!state.parentSelected || state.parentSelected === 'all') {
      setLoading(false);

      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const children = await fetchSubOrganizations(state.parentSelected);
        if (cancelled) return;
        dispatch({
          type: ActionTypes.SET_SUB_ORGANIZATIONS,
          data: children.map(item => ({
            label: (item.name?.en as string) ?? '',
            value: String(item.id)
          }))
        });
      } catch {
        // swallow — empty sub list on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [state.parentSelected]);

  useEffect(() => {
    const selected = state.parentSelected || state.subSelected;

    if (selected) {
      dispatch({
        type: ActionTypes.SELECT_PARENT_ORGANIZATION,
        data: selected
      });
    }
  }, [state.parentSelected, state.subSelected]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <OrganizationsStateContext.Provider value={state}>
      <OrganizationsDispatchContext.Provider value={dispatch}>
        {children}
      </OrganizationsDispatchContext.Provider>
    </OrganizationsStateContext.Provider>
  );
};

const useOrganizationsStateContext = (): State => {
  const context = useContext(OrganizationsStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useOrganizationsStateContext must be used within a OrganizationsProvider'
    );
  }

  return context;
};

const useOrganizationsDispatchContext = (): DispatchContext => {
  const dispatch = useContext(OrganizationsDispatchContext);
  const [searchParams, setSearchParams] = useSearchParams();

  if (typeof dispatch === 'undefined') {
    throw new Error(
      'useOrganizationsDispatchContext must be used within a OrganizationsProvider'
    );
  }

  // Replaces the legacy client.refetchQueries(...) cache invalidation
  // with a monotonic counter on context. Page-level fetch useEffects
  // add `orgVersion` to their deps to re-run on org switch.
  const resetData = useCallback(() => {
    dispatch({ type: ActionTypes.BUMP_ORG_VERSION });
  }, [dispatch]);

  const onChangeParent: DispatchContext['onChangeParent'] = useCallback(
    value => {
      parentOrgStorage.set(value);
      subOrgStorage.remove();
      dispatch({ type: ActionTypes.SELECT_PARENT_ORGANIZATION, data: value });

      if (searchParams.get('page')) {
        searchParams.delete('page');
        setSearchParams(searchParams);
      }

      resetData();
    },
    [dispatch, resetData, searchParams, setSearchParams]
  );

  const onChangeSub: DispatchContext['onChangeSub'] = useCallback(
    value => {
      subOrgStorage.set(value);
      dispatch({ type: ActionTypes.SELECT_SUB_ORGANIZATION, data: value });

      if (searchParams.get('page')) {
        searchParams.delete('page');
        setSearchParams(searchParams);
      }

      resetData();
    },
    [dispatch, resetData, searchParams, setSearchParams]
  );

  return { onChangeSub, onChangeParent };
};

export default OrganizationsProvider;
export { useOrganizationsDispatchContext, useOrganizationsStateContext };
